/**
 * Sun, planets and Pluto: spheres at their display size, minimum-size markers, orbit lines.
 */
import * as THREE from 'three';
import {
  type BodyOrientation,
  ORBITAL_PERIOD_DAYS,
  type EphemerisBodyId,
  type RotatingBodyId,
  bodyOrientation,
  isEphemerisBody,
  orbitPoints,
} from '../core/ephemeris';
import type { OrbitsFile } from '../core/data';
import { length } from '../core/vec3';
import { vec3 } from '../core/vec3';
import { MOON_PERIOD_DAYS, analyticMoonOffset, isAnalyticMoon, moonBlur } from '../core/moons';
import { placeRelative } from './floatingOrigin';
import { atmosphereUniforms, createAtmosphere } from './atmosphere';
import { createSunMaterial } from './sun';
import { discTexture, glowTexture, placeholderTexture } from './textures';

/** HDR gains for the post-processed tiers (S2-07): the Sun and its glow are the brightest things in the scene. */
const SUN_HDR = 2.2;
const SUN_GLOW_HDR = 1;
const SUN_LIGHT = 2.6;
/** Moon orbit rings (fast playback): samples per orbit, opacity at full blur, recompute interval (days). */
const MOON_RING_SAMPLES = 96;
/**
 * Ring samples computed per frame (S4-03): one ring with the full lunar theory took 130 ms of a phone's first frame,
 * and returned every 200 mission days at fast playback. A new ring takes 12 frames; the old one stays until then.
 */
const MOON_RING_CHUNK = 8;
const MOON_RING_OPACITY = 0.45;
/**
 * Moons whose orbit ring stays on at the planets' orbit-line opacity, not only at fast playback (S4-06, D2): the Moon
 * around Earth. The Galilean moons keep the Sprint 2 behaviour (rings only when they blur).
 */
const ORBIT_LINE_MOONS = new Set(['moon']);
const MOON_RING_REFRESH_DAYS = 200;
/** Planet orbits recede behind the spacecraft trail (S2-09). */
const ORBIT_OPACITY = 0.24;
import type { World, WorldBody } from './world';

const ORBIT_SAMPLES = 360;
const ORBIT_REFRESH_DAYS = 3652;
const RING_RADIAL = 24;
const RING_ANGULAR = 128;
const RING_COS = Float64Array.from({ length: RING_ANGULAR + 1 }, (_, j) => Math.cos((2 * Math.PI * j) / RING_ANGULAR));
const RING_SIN = Float64Array.from({ length: RING_ANGULAR + 1 }, (_, j) => Math.sin((2 * Math.PI * j) / RING_ANGULAR));

/** Flat ring in the body's equatorial plane (local XZ); u runs from the inner to the outer edge. */
function createRingMesh(): THREE.Mesh {
  const n = (RING_RADIAL + 1) * (RING_ANGULAR + 1);
  const uv = new Float32Array(2 * n);
  const index: number[] = [];
  for (let i = 0; i <= RING_RADIAL; i++) {
    for (let j = 0; j <= RING_ANGULAR; j++) {
      const k = i * (RING_ANGULAR + 1) + j;
      uv[2 * k] = i / RING_RADIAL;
      uv[2 * k + 1] = 0.5;
      if (i < RING_RADIAL && j < RING_ANGULAR) {
        const a = k;
        const b = k + 1;
        const c = k + RING_ANGULAR + 1;
        const d = c + 1;
        index.push(a, c, b, b, c, d);
      }
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3 * n), 3));
  geom.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geom.setIndex(index);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xd9c9a3,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  return new THREE.Mesh(geom, mat);
}

/** Spheres smaller than this on screen (px) are not drawn; their marker stands for them. */
const SPHERE_MIN_PX = 1;

interface BodyVisual {
  body: WorldBody;
  group: THREE.Group;
  /** Body-fixed frame: local X = prime meridian, local Y = north pole. */
  oriented: THREE.Group;
  mesh: THREE.Mesh;
  rings?: THREE.Mesh;
  marker: THREE.Sprite;
  /** Galilean moons (S2 feedback): orbit ring shown instead of the moon at fast playback. */
  moonRing?: {
    line: THREE.LineLoop;
    positions: Float32Array;
    /** The shown ring's true offsets from the planet (km; NaN where there is none) and its start (TDB days). */
    q: Float64Array;
    computedAt: number;
    /** The next ring while it is being sampled, a few samples per frame: offsets, start (NaN: none), samples done. */
    next: Float64Array;
    nextAt: number;
    nextDone: number;
    /** Profile version `positions` are mapped with. */
    profileVersion: number;
  };
  /** 0 = solid moon, 1 = only its ring (fast playback). */
  blur: number;
  orbit?: {
    line: THREE.LineLoop;
    samples: number;
    true: Float64Array;
    /** Mapped display positions (du, float64); the float32 vertices are written relative to the planet. */
    disp: Float64Array;
    center: number;
    period: number;
  };
}

export interface ViewInfo {
  /** Pixels per du at distance 1 from the camera (screen height / (2·tan(fov/2))). */
  pxPerDuAtUnit: number;
  camera: THREE.Camera;
}

export class Bodies {
  readonly visuals: BodyVisual[] = [];
  readonly root = new THREE.Group();
  readonly sunLight = new THREE.PointLight(0xffffff, SUN_LIGHT, 0, 0);
  private readonly sunGlow: THREE.Sprite;
  private sunMaterial: THREE.ShaderMaterial | null = null;
  private hdr = false;
  private speedDaysPerS = 0;
  private ignition = 1;
  private readonly hdrMaterials: { material: { color: THREE.Color }; base: THREE.Color; gain: number }[] = [];
  private orbitProfileVersion = -1;
  private ringProfileVersion = -1;
  private readonly orientation: BodyOrientation = { north: vec3(), prime: vec3() };
  private readonly basis = new THREE.Matrix4();
  private readonly ax = new THREE.Vector3();
  private readonly ay = new THREE.Vector3();
  private readonly az = new THREE.Vector3();
  private readonly tmp = vec3();
  private readonly worldPos = new THREE.Vector3();
  private readonly ringQ = vec3();
  private readonly ringCentre = new THREE.Vector3();

  constructor(private readonly world: World) {
    const disc = discTexture();
    // one unit sphere for every body (scaled per body): fewer allocations at start-up (S3-02)
    let sphere: THREE.SphereGeometry | null = null;
    for (const body of world.bodies) {
      const isSun = body.data.id === 'sun';
      const color = new THREE.Color(body.data.color);
      const segments = 64;
      // Textured bodies start with a 1×1 white map (S3-02): the shader variant with a map is then compiled up front,
      // and the real texture replaces it later without a recompile. Moons are transparent from the start for the
      // same reason (their blur into an orbit ring fades the opacity).
      const material = isSun
        ? (this.sunMaterial = createSunMaterial(color))
        : new THREE.MeshStandardMaterial({
            color,
            roughness: 0.95,
            metalness: 0,
            map: body.data.texture ? placeholderTexture() : null,
            transparent: !!body.parent,
          });
      const mesh = new THREE.Mesh(sphere ?? (sphere = new THREE.SphereGeometry(1, segments, segments / 2)), material);
      mesh.name = body.data.id;
      // atmosphere rim as a child of the mesh, so it scales with the display radius (S2-08)
      const atm = body.data.atmosphere;
      if (atm) mesh.add(createAtmosphere(atm.color, atm.strength ?? 1));
      const marker = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: disc, color, sizeAttenuation: false, transparent: true, depthWrite: false }),
      );
      marker.renderOrder = 2;
      const group = new THREE.Group();
      const oriented = new THREE.Group();
      oriented.add(mesh);
      group.add(oriented, marker);
      this.root.add(group);
      const visual: BodyVisual = { body, group, oriented, mesh, marker, blur: 0 };
      // moons that blur into an orbit ring at fast playback (Galilean moons; Titan, S3-06)
      if (MOON_PERIOD_DAYS[body.data.id] !== undefined) {
        const positions = new Float32Array(3 * MOON_RING_SAMPLES);
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        const line = new THREE.LineLoop(
          geom,
          new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0, depthWrite: false }),
        );
        line.frustumCulled = false;
        line.visible = false;
        this.root.add(line);
        visual.moonRing = {
          line,
          positions,
          q: new Float64Array(3 * MOON_RING_SAMPLES),
          computedAt: Number.NaN,
          next: new Float64Array(3 * MOON_RING_SAMPLES),
          nextAt: Number.NaN,
          nextDone: 0,
          profileVersion: -1,
        };
      }
      if (body.data.rings) {
        visual.rings = createRingMesh();
        (visual.rings.material as THREE.MeshBasicMaterial).map = placeholderTexture();
        oriented.add(visual.rings);
      }
      if (!isSun && !body.parent && isEphemerisBody(body.data.id)) {
        const id = body.data.id as Exclude<EphemerisBodyId, 'sun'>;
        const samples = id === 'pluto' ? ORBIT_SAMPLES / 2 : ORBIT_SAMPLES;
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3 * samples), 3));
        const line = new THREE.LineLoop(
          geom,
          new THREE.LineBasicMaterial({ color, transparent: true, opacity: ORBIT_OPACITY, depthWrite: false }),
        );
        line.frustumCulled = false;
        line.visible = false; // until its samples exist
        this.root.add(line);
        // build-time tables (S3-02): sampling the orbits with VSOP/Pluto's integration cost up to ~200 ms per planet
        // in the first frames; they arrive after the first frame (setOrbits), until then the line waits (center ∞)
        visual.orbit = {
          line,
          samples,
          true: new Float64Array(0),
          disp: new Float64Array(3 * samples),
          center: Infinity,
          period: ORBITAL_PERIOD_DAYS[id],
        };
      }
      this.visuals.push(visual);
    }
    this.sunGlow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture(),
        color: 0xffd27a,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.root.add(this.sunGlow, this.sunLight, new THREE.AmbientLight(0xffffff, 0.06));
    const sun = this.visuals.find((v) => v.body.data.id === 'sun');
    if (sun) {
      this.hdrMaterials.push({ material: sun.marker.material, base: sun.marker.material.color.clone(), gain: SUN_HDR });
    }
    this.hdrMaterials.push({
      material: this.sunGlow.material,
      base: this.sunGlow.material.color.clone(),
      gain: SUN_GLOW_HDR,
    });
  }

  /**
   * HDR on/off (S2-07): with post-processing the Sun is pushed above 1 so that only it blooms; without it the
   * colours stay in [0, 1] (otherwise they would clip to white).
   */
  setHdr(on: boolean): void {
    this.hdr = on;
    this.applySunGain();
  }

  /** Intro (S2-12): the Sun "ignites" from 0 to full brightness. */
  setIgnition(k: number): void {
    const v = Math.min(Math.max(k, 0), 1);
    if (v === this.ignition) return;
    this.ignition = v;
    this.applySunGain();
  }

  private applySunGain(): void {
    const k = this.ignition;
    for (const h of this.hdrMaterials) h.material.color.copy(h.base).multiplyScalar((this.hdr ? h.gain : 1) * k);
    if (this.sunMaterial) {
      (this.sunMaterial.uniforms['uGain'] as THREE.IUniform<number>).value = (this.hdr ? SUN_HDR : 1) * k;
    }
    this.sunLight.intensity = SUN_LIGHT * k;
  }

  /** Advances surface animations (the Sun's granulation) to `seconds` of real time; constant = frozen. */
  animate(seconds: number): void {
    if (this.sunMaterial) (this.sunMaterial.uniforms['uTime'] as THREE.IUniform<number>).value = seconds;
  }

  /**
   * Recomputes orbit samples when time has moved far from their centre. At most one orbit per frame, so start-up and
   * playback never stall (Pluto's orbit alone costs ~30 ms of ephemeris work). Returns true if one changed.
   */
  private refreshOrbitSamples(tdb: number): boolean {
    for (const v of this.visuals) {
      const o = v.orbit;
      // Orbits change only secularly; one resample per decade (or half a period) is plenty and avoids hitches
      // from 360 ephemeris evaluations during fast playback.
      if (!o || o.center === Infinity || Math.abs(tdb - o.center) < Math.max(o.period / 2, ORBIT_REFRESH_DAYS))
        continue;
      o.true = orbitPoints(v.body.data.id as Exclude<EphemerisBodyId, 'sun'>, tdb, o.samples);
      o.center = tdb;
      o.line.visible = true;
      return true;
    }
    return false;
  }

  /**
   * Installs the build-time orbit tables (orbits.json, loaded after the first frame, S4-03 part 6); a body without
   * one, or all of them if the file failed (null), samples its orbit at run time, one per frame.
   */
  setOrbits(tables: OrbitsFile | null): void {
    for (const v of this.visuals) {
      const o = v.orbit;
      if (!o) continue;
      const table = tables?.[v.body.data.id];
      if (!table) {
        o.center = Number.NaN;
        continue;
      }
      o.samples = table.length / 3;
      o.true = Float64Array.from(table);
      o.disp = new Float64Array(table.length);
      o.line.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(table.length), 3));
      o.line.visible = true;
    }
    // re-mapped in the next update
    this.orbitProfileVersion = -1;
  }

  /** Re-maps the orbit lines through the global mapping (on profile change or new samples). */
  private remapOrbits(): void {
    const m = this.world.mapping;
    const p = vec3();
    for (const v of this.visuals) {
      const o = v.orbit;
      if (!o || o.true.length === 0) continue;
      for (let i = 0; i < o.samples; i++) {
        p.x = o.true[3 * i]!;
        p.y = o.true[3 * i + 1]!;
        p.z = o.true[3 * i + 2]!;
        m.mapGlobal(p, p);
        o.disp[3 * i] = p.x;
        o.disp[3 * i + 1] = p.y;
        o.disp[3 * i + 2] = p.z;
      }
    }
  }

  /**
   * Writes an orbit line's float32 vertices relative to its planet (float64 subtraction), so the line stays exact
   * where the planet is, even zoomed in on Neptune in Realistic mode (spike Q7).
   */
  private anchorOrbit(v: BodyVisual, origin: { x: number; y: number; z: number }): void {
    const o = v.orbit!;
    const d = v.body.d;
    const attr = o.line.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    for (let i = 0; i < 3 * o.samples; i += 3) {
      arr[i] = o.disp[i]! - d.x;
      arr[i + 1] = o.disp[i + 1]! - d.y;
      arr[i + 2] = o.disp[i + 2]! - d.z;
    }
    attr.needsUpdate = true;
    o.line.position.set(d.x - origin.x, d.y - origin.y, d.z - origin.z);
  }

  update(origin: { x: number; y: number; z: number }, profileVersion: number, markerPx: number, view: ViewInfo): void {
    const samplesChanged = this.refreshOrbitSamples(this.world.tdb);
    if (samplesChanged || profileVersion !== this.orbitProfileVersion) {
      this.remapOrbits();
      this.orbitProfileVersion = profileVersion;
    }
    const ringsDirty = profileVersion !== this.ringProfileVersion;
    this.ringProfileVersion = profileVersion;
    for (const v of this.visuals) {
      // HORIZONS tables (Titan, Arrokoth) cover only some windows: hidden outside them
      v.group.visible = v.body.active;
      if (!v.body.active) continue;
      placeRelative(v.group, v.body.d, origin);
      v.mesh.scale.setScalar(v.body.r);
      this.orient(v);
      if (v.rings && ringsDirty) this.updateRings(v);
      if (v.orbit && v.orbit.true.length > 0) this.anchorOrbit(v, origin);

      // Marker at minimum pixel size; fades out once the sphere itself is larger than the marker.
      v.group.getWorldPosition(this.worldPos);
      const dist = Math.max(this.worldPos.distanceTo(view.camera.position), 1e-12);
      const meshPx = (v.body.r / dist) * view.pxPerDuAtUnit;
      const size = markerPx / view.pxPerDuAtUnit;
      v.marker.scale.set(size, size, 1);
      const material = v.marker.material;
      material.opacity = Math.min(1, Math.max(0, 1.5 - meshPx / (markerPx / 2)));
      v.marker.visible = material.opacity > 0.01;
      // a sphere below a pixel adds nothing but a draw call (lit, textured, with atmosphere): the marker shows it
      // (S4-15: phones spent most of a frame on draw calls; 15 spheres were drawn in the overview, most sub-pixel)
      v.oriented.visible = meshPx >= SPHERE_MIN_PX;
      // a moon whose marker sits on its planet's (the planet's bubble is not open on screen): only the planet's
      const parent = v.body.parent;
      if (parent && v.marker.visible) {
        const sepPx =
          (Math.hypot(v.body.d.x - parent.d.x, v.body.d.y - parent.d.y, v.body.d.z - parent.d.z) / dist) *
          view.pxPerDuAtUnit;
        if (sepPx < markerPx) v.marker.visible = false;
      }
      if (v.moonRing) this.updateMoon(v, origin, profileVersion);
    }
    const sun = this.world.bodyById.get('sun')!;
    this.tmp.x = sun.d.x - origin.x;
    this.tmp.y = sun.d.y - origin.y;
    this.tmp.z = sun.d.z - origin.z;
    this.sunLight.position.set(this.tmp.x, this.tmp.y, this.tmp.z);
    atmosphereUniforms.uSunPos.value.copy(this.sunLight.position);
    this.sunGlow.position.copy(this.sunLight.position);
    this.sunGlow.scale.setScalar(sun.r * 8);
  }

  /** Mission days per real second of the current playback (0 while paused); drives the moon blur. */
  setPlaybackSpeed(daysPerSecond: number): void {
    this.speedDaysPerS = daysPerSecond;
  }

  /** How far a body is blurred into its orbit ring (moons at fast playback; 0 for everything else). */
  blurOf(id: string): number {
    return this.visuals.find((v) => v.body.data.id === id)?.blur ?? 0;
  }

  /**
   * Galilean moons: above ~90° of orbit per real second a moon cannot be followed any more and would jump around the
   * planet; it then dissolves into a faint ring of its orbit (and comes back in slow-motion near a flyby).
   */
  private updateMoon(v: BodyVisual, origin: { x: number; y: number; z: number }, profileVersion: number): void {
    const ring = v.moonRing!;
    const blur = moonBlur(MOON_PERIOD_DAYS[v.body.data.id]!, this.speedDaysPerS);
    v.blur = blur;
    const mat = v.mesh.material as THREE.MeshStandardMaterial;
    const solid = 1 - blur;
    mat.opacity = solid;
    v.mesh.visible = solid > 0.02;
    v.marker.material.opacity *= solid;
    v.marker.visible &&= v.marker.material.opacity > 0.01;
    const ringOpacity = Math.max(ORBIT_LINE_MOONS.has(v.body.data.id) ? ORBIT_OPACITY : 0, MOON_RING_OPACITY * blur);
    ring.line.visible = ringOpacity > 0.01;
    if (!ring.line.visible) return;
    (ring.line.material as THREE.LineBasicMaterial).opacity = ringOpacity;
    const tdb = this.world.tdb;
    const stale = (at: number): boolean => Number.isNaN(at) || Math.abs(tdb - at) > MOON_RING_REFRESH_DAYS;
    if (stale(ring.computedAt) && stale(ring.nextAt)) {
      ring.nextAt = tdb;
      ring.nextDone = 0;
    }
    if (!Number.isNaN(ring.nextAt) && this.sampleMoonRing(v)) {
      [ring.q, ring.next] = [ring.next, ring.q];
      ring.computedAt = ring.nextAt;
      ring.nextAt = Number.NaN;
      ring.profileVersion = -1;
    }
    if (Number.isNaN(ring.computedAt)) {
      ring.line.visible = false;
      return;
    }
    if (ring.profileVersion !== profileVersion) {
      this.mapMoonRing(v);
      ring.profileVersion = profileVersion;
    }
    placeRelative(ring.line, v.body.parent!.d, origin);
  }

  /** Samples the next ring's true offsets, MOON_RING_CHUNK per call; true when the ring is complete. */
  private sampleMoonRing(v: BodyVisual): boolean {
    const id = v.body.data.id;
    const ring = v.moonRing!;
    const period = MOON_PERIOD_DAYS[id]!;
    const q = this.ringQ;
    const end = Math.min(MOON_RING_SAMPLES, ring.nextDone + MOON_RING_CHUNK);
    for (let k = ring.nextDone; k < end; k++) {
      const t = ring.nextAt + (k / MOON_RING_SAMPLES) * period;
      // only this moon (S4-03: all five analytic moons per sample cost the Galilean rings the full lunar theory)
      const ok = isAnalyticMoon(id)
        ? !!analyticMoonOffset(id, t, q)
        : !!v.body.table?.at(t, q) || !!v.body.table?.at(t - period, q);
      ring.next[3 * k] = ok ? q.x : Number.NaN;
      ring.next[3 * k + 1] = ok ? q.y : Number.NaN;
      ring.next[3 * k + 2] = ok ? q.z : Number.NaN;
    }
    ring.nextDone = end;
    return end === MOON_RING_SAMPLES;
  }

  /** Maps the ring like the moon itself, stored relative to the planet's display position. */
  private mapMoonRing(v: BodyVisual): void {
    const ring = v.moonRing!;
    const parent = v.body.parent!;
    const out = this.tmp;
    const q = this.ringQ;
    for (let k = 0; k < MOON_RING_SAMPLES; k++) {
      q.x = ring.q[3 * k]!;
      q.y = ring.q[3 * k + 1]!;
      q.z = ring.q[3 * k + 2]!;
      if (Number.isNaN(q.x)) continue;
      this.world.mapping.mapRelative(parent.mapped, parent.p, q, out);
      ring.positions[3 * k] = out.x - parent.d.x;
      ring.positions[3 * k + 1] = out.y - parent.d.y;
      ring.positions[3 * k + 2] = out.z - parent.d.z;
    }
    (ring.line.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }

  /** Rotates the body-fixed frame (spin axis and prime meridian, S1-23); the Moon too (S4-06: near side to Earth). */
  private orient(v: BodyVisual): void {
    const id = v.body.data.id;
    if (id === 'sun' || (id !== 'moon' && (v.body.parent || !isEphemerisBody(id)))) return;
    const o = bodyOrientation(id as RotatingBodyId, this.world.tdb, this.orientation);
    this.ax.set(o.prime.x, o.prime.y, o.prime.z);
    this.ay.set(o.north.x, o.north.y, o.north.z);
    this.az.crossVectors(this.ax, this.ay);
    this.basis.makeBasis(this.ax, this.ay, this.az);
    v.oriented.quaternion.setFromRotationMatrix(this.basis);
  }

  /** Ring vertices: true radii mapped through the same altitude mapping as spacecraft in the bubble. */
  private updateRings(v: BodyVisual): void {
    const rings = v.body.data.rings!;
    const m = this.world.mapping;
    const r = length(v.body.p);
    const attr = v.rings!.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    let rDisp = 0;
    for (let i = 0; i <= RING_RADIAL; i++) {
      const rTrue = rings.inner + ((rings.outer - rings.inner) * i) / RING_RADIAL;
      rDisp = v.body.r + m.altitude(v.body.mapped, r, rTrue - v.body.mapped.radius);
      for (let j = 0; j <= RING_ANGULAR; j++) {
        const k = 3 * (i * (RING_ANGULAR + 1) + j);
        arr[k] = rDisp * RING_COS[j]!;
        arr[k + 1] = 0;
        arr[k + 2] = rDisp * RING_SIN[j]!;
      }
    }
    attr.needsUpdate = true;
    // the outer edge bounds the flat ring: no pass over its 3,225 vertices (S4-03: 35 ms of a phone's first frame)
    const geom = v.rings!.geometry;
    (geom.boundingSphere ??= new THREE.Sphere()).set(this.ringCentre, rDisp);
  }

  /**
   * Lazily loads textures after the first render (S1-20); `size` is '1k' on phones, '2k' otherwise. Images are decoded
   * off the main thread (ImageBitmap where available) and queued; `uploadNext` puts one per frame on the GPU, so no
   * single frame carries several uploads (S3-02). Resolves when every texture is on the GPU (or failed).
   */
  loadTextures(size: '1k' | '2k'): Promise<void> {
    const url = (base: string, ext: string): string => `${import.meta.env.BASE_URL}${base}-${size}.${ext}`;
    const bitmap = typeof createImageBitmap === 'function';
    const load = (src: string): Promise<THREE.Texture | null> =>
      new Promise((resolve) => {
        if (bitmap) {
          // WebGL ignores UNPACK_FLIP_Y for ImageBitmaps: flip while decoding instead
          const loader = new THREE.ImageBitmapLoader().setOptions({ imageOrientation: 'flipY' });
          loader.load(
            src,
            (image) => {
              const tex = new THREE.Texture(image);
              tex.flipY = false;
              resolve(tex);
            },
            undefined,
            () => resolve(null),
          );
        } else {
          new THREE.TextureLoader().load(src, resolve, undefined, () => resolve(null));
        }
      });
    const jobs: Promise<void>[] = [];
    const queue = (src: string, apply: (tex: THREE.Texture) => void): void => {
      jobs.push(
        load(src).then(
          (tex) =>
            new Promise<void>((done) => {
              if (!tex) return done();
              tex.colorSpace = THREE.SRGBColorSpace;
              tex.needsUpdate = true;
              this.uploads.push({ tex, apply, done });
            }),
        ),
      );
    };
    for (const v of this.visuals) {
      const data = v.body.data;
      if (data.texture) {
        queue(url(data.texture, 'jpg'), (tex) => {
          tex.anisotropy = 4;
          const mat = v.mesh.material as THREE.MeshStandardMaterial;
          mat.map = tex;
          mat.color.set(0xffffff);
        });
      }
      if (data.rings && v.rings) {
        queue(url(data.rings.texture, 'png'), (tex) => {
          const mat = v.rings!.material as THREE.MeshBasicMaterial;
          mat.map = tex;
          mat.color.set(0xffffff);
          mat.opacity = 1;
        });
      }
    }
    return Promise.all(jobs).then(() => undefined);
  }

  private readonly uploads: { tex: THREE.Texture; apply: (tex: THREE.Texture) => void; done: () => void }[] = [];

  /** Uploads at most one loaded texture to the GPU and puts it on its material (call once per frame). */
  uploadNext(renderer: THREE.WebGLRenderer): void {
    const job = this.uploads.shift();
    if (!job) return;
    renderer.initTexture(job.tex);
    job.apply(job.tex);
    job.done();
  }

  /** Screen-projected radius in px of a body (for picking and label placement). */
  projectedRadiusPx(id: string, view: ViewInfo): number {
    const v = this.visuals.find((x) => x.body.data.id === id);
    if (!v) return 0;
    v.group.getWorldPosition(this.worldPos);
    const dist = Math.max(this.worldPos.distanceTo(view.camera.position), 1e-12);
    return (v.body.r / dist) * view.pxPerDuAtUnit;
  }
}
